import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users2, CheckCircle2, XCircle, AlertTriangle, Landmark, FileSpreadsheet,
  FileText, Upload, UserCog, ScrollText, Settings as SettingsIcon, LogOut, Menu, X, KeyRound, ClipboardList, BadgeIndianRupee,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", mr: "डॅशबोर्ड", icon: LayoutDashboard },
  { to: "/beneficiaries", label: "All Beneficiaries", mr: "सर्व लाभार्थी", icon: Users2 },
  { to: "/beneficiaries?tab=eligible", label: "Eligible", mr: "पात्र", icon: CheckCircle2 },
  { to: "/beneficiaries?tab=ineligible", label: "Ineligible", mr: "अपात्र", icon: XCircle },
  { to: "/beneficiaries?tab=error", label: "Error / Deficiency", mr: "त्रुटी", icon: AlertTriangle },
  { to: "/beneficiaries?tab=no_bank", label: "Without Bank A/c", mr: "बँक खाते नाही", icon: Landmark },
  { to: "/attendance-form", label: "Attendance Form", mr: "उपस्थिती नमुना", icon: ClipboardList },
  { to: "/reports", label: "Reports", mr: "अहवाल", icon: FileText },
  { to: "/import", label: "Excel Import", mr: "एक्सेल आयात", icon: Upload },
  { to: "/installment-import", label: "Installment Import", mr: "हप्ता यादी आयात", icon: BadgeIndianRupee },
  { to: "/users", label: "User Management", mr: "वापरकर्ते", icon: UserCog, perm: "users" },
  { to: "/audit-logs", label: "Audit Logs", mr: "ऑडिट लॉग", icon: ScrollText, perm: "audit" },
  { to: "/settings", label: "Settings", mr: "सेटिंग्ज", icon: SettingsIcon },
];

const ROLE_LABEL = {
  super_admin: "Super Admin", data_entry_operator: "Data Entry Operator",
  verifier: "Verifier", report_user: "Report User",
};

export const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = NAV.filter((n) => !n.perm || can(n.perm));

  return (
    <div className="min-h-screen bg-[#f4f6fa] text-slate-800">
      <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-slate-200 bg-[#0f2545] px-4 text-white shadow-sm">
        <button data-testid="sidebar-toggle" onClick={() => setOpen(!open)} className="rounded-md p-2 hover:bg-white/10 lg:hidden">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
        <Link to="/" className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded bg-amber-400 font-bold text-[#0f2545]">स्व</div>
          <div className="leading-tight">
            <div className="font-serif text-sm font-semibold tracking-wide sm:text-base">Swadhar Scheme BDMS</div>
            <div className="hidden text-[11px] text-slate-300 sm:block">स्वाधार योजना लाभार्थी डेटाबेस · जिल्हा लातूर</div>
          </div>
        </Link>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden text-right sm:block">
            <div data-testid="header-username" className="text-sm font-medium">{user?.full_name || user?.username}</div>
            <div className="text-[11px] text-amber-300">{ROLE_LABEL[user?.role] || user?.role}</div>
          </div>
          <button data-testid="change-password-btn" onClick={() => navigate("/settings")} title="Change password" className="rounded-md p-2 hover:bg-white/10">
            <KeyRound size={18} />
          </button>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20">
            <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="flex">
        <aside className={`fixed inset-y-16 left-0 z-30 w-72 overflow-y-auto border-r border-slate-200 bg-white transition-transform lg:static lg:inset-auto lg:h-[calc(100vh-4rem)] lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
          <nav className="space-y-1 p-3">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive && window.location.search === new URL(n.to, window.location.origin).search
                      ? "bg-[#0f2545] text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
              >
                <n.icon size={17} />
                <span className="flex-1">{n.label}</span>
                <span className="text-[10px] text-slate-400">{n.mr}</span>
              </NavLink>
            ))}
          </nav>
        </aside>
        {open && <div className="fixed inset-0 z-20 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}
        <main className="min-w-0 flex-1 p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
};
